/**
 * TWAP report — the pure half (TASK-20260823-twap-strategies-tca, PR-A2).
 *
 * Everything the per-parent report renders is derived here from the read models the API returns
 * (contract: Exchange.Data/Models/TwapReadModels.cs + Data.Models/Twap/TwapScorecardResult.cs,
 * camelCase on the wire) so the charts and tiles stay thin and the interleavings are unit-testable.
 *
 * Two rules from spec AC 10 are load-bearing and visible in the shapes below:
 *   - the savings identity `NetSavings = PriceImprovement + FeesSaved` is one series; implementation
 *     shortfall `IS = executionCost + opportunityCost` is a SEPARATE series; market drift is context,
 *     never a step in either;
 *   - a missing input is an honest null rendered as "—" with the scorecard's note, never a fake zero.
 * Every signed figure carries an explicit sign AND an arrow so gain/loss is never color-alone.
 */
import { formatQuoteAmount } from './quoteCurrency.js';
import { POSTURE_NAMES } from './twapPolicy.js';
import { toMs } from '../components/shared/charts/svgScales.js';

/** The pace band the pace chart draws around the schedule line (± percent of the schedule). */
export const PACE_BAND_PCT = 2;

/** The parent-id prefix TwapIds.cs stamps on every synthetic parent (never a venue GUID). */
export const PARENT_ID_PREFIX = 'twp';

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

// ----- sign helpers (named, never inline constants) -------------------------------------------

/** +1 for a buy, −1 for a sell (the loop's slippage convention). Unknown → +1. */
export function sideSign(side) {
  return String(side ?? '').toLowerCase() === 'sell' ? -1 : 1;
}

const MINUS = '−'; // U+2212, not a hyphen — reads as a sign in tabular figures.
const UP = '▲';
const DOWN = '▼';

function signed(text, v, { signed: withSign = true } = {}) {
  if (!withSign || v === 0) return text;
  return v > 0 ? `+${text} ${UP}` : `${MINUS}${text} ${DOWN}`;
}

/** `+5.0 bps ▲` / `−5.0 bps ▼` / `0.0 bps` / `—`. */
export function fmtBps(v, opts = {}) {
  if (!isNum(v)) return '—';
  return signed(`${Math.abs(v).toFixed(opts.decimals ?? 1)} bps`, v, opts);
}

/** `+0.35 % ▲` (signed by default); `{ signed: false }` for a share such as maker fill. */
export function fmtPct(v, opts = {}) {
  if (!isNum(v)) return '—';
  return signed(`${Math.abs(v).toFixed(opts.decimals ?? 2)} %`, v, opts);
}

/** `+$350.15 ▲` / `−$12.50 ▼` / `$0.00` / `—`, in the parent's quote currency. */
export function fmtQuote(v, currency = 'USD', opts = {}) {
  if (!isNum(v)) return '—';
  return signed(formatQuoteAmount(Math.abs(v), currency), v, opts);
}

/** A base-unit quantity without trailing zeros (1 → `1`, 0.4 → `0.4`). */
export function fmtQty(v) {
  if (!isNum(v)) return '—';
  return Number(v.toFixed(8)).toString();
}

/** Signed tone for a "positive = good" figure. */
function toneOf(v) {
  if (!isNum(v) || v === 0) return 'neutral';
  return v > 0 ? 'gain' : 'loss';
}

// ----- status ---------------------------------------------------------------------------------

/** The durable TwapParentStatus names → a chip tone. */
export function statusTone(status) {
  switch (status) {
    case 'Working': return 'working';
    case 'Done': return 'done';
    case 'PartialFilled': return 'partial';
    case 'Cancelled': return 'cancelled';
    case 'Rejected':
    case 'Abandoned': return 'failed';
    default: return 'neutral';
  }
}

export const STATUS_LABELS = Object.freeze({
  Working: 'Working', Done: 'Done', PartialFilled: 'Partially filled', Cancelled: 'Cancelled',
  Rejected: 'Rejected', Abandoned: 'Abandoned',
});

/** `Working` is the ONE non-terminal state. */
export function isWorking(status) {
  return status === 'Working';
}

/** Is this order id a synthetic TWAP parent (TwapIds.IsParentId)? */
export function isParentId(orderId) {
  return typeof orderId === 'string' && orderId.length > PARENT_ID_PREFIX.length && orderId.startsWith(PARENT_ID_PREFIX);
}

/**
 * The FeeRateSource badge: paper sources are flagged so a 0.6 % / 1.2 % paper schedule is never
 * read as live savings (spec AC 25).
 */
export function feeSourceBadge(source) {
  if (!source) return { label: 'fee source unknown', paper: false, known: false };
  const paper = /paper|default/i.test(source);
  const labels = {
    'paper-schedule': 'Paper fee schedule',
    'coinbase-transaction-summary': 'Coinbase fee tier',
    default: 'Default fee assumption',
  };
  return { label: labels[source] ?? source, paper, known: true };
}

// ----- scorecard tiles ------------------------------------------------------------------------

/**
 * Hero KPI tiles. Reads the SCORECARD only — a Working parent has none yet, so every tile is an
 * honest "—" rather than a stale summary number.
 */
export function kpis(parent, scorecard) {
  const sc = scorecard ?? {};
  const ccy = parent?.quoteCurrency ?? 'USD';
  const tile = (key, label, value, text, tone, sub = null, hint = null) => ({ key, label, value: isNum(value) ? value : null, text, tone, sub, hint });
  return [
    tile('netSavings', 'Net savings vs sweep', sc.netSavingsQuote, fmtQuote(sc.netSavingsQuote, ccy), toneOf(sc.netSavingsQuote),
      isNum(sc.netSavingsPct) ? fmtPct(sc.netSavingsPct) : null, 'price improvement + fees saved; drift excluded'),
    tile('feesSaved', 'Fees saved', sc.feesSaved, fmtQuote(sc.feesSaved, ccy), toneOf(sc.feesSaved),
      isNum(sc.takerEquivalentFees) ? `vs ${fmtQuote(sc.takerEquivalentFees, ccy, { signed: false })} all-taker` : null),
    tile('makerFill', 'Maker fill', sc.makerFillPct, fmtPct(sc.makerFillPct, { signed: false, decimals: 1 }), 'neutral',
      isNum(sc.makerChildCount) && isNum(sc.childCount) ? `${sc.makerChildCount} of ${sc.childCount} slices` : null),
    tile('vsSweep', 'vs sweep', sc.improvementVsSweepBps, fmtBps(sc.improvementVsSweepBps), toneOf(sc.improvementVsSweepBps),
      isNum(sc.sweepPriceForSize) ? `sweep ${formatQuoteAmount(sc.sweepPriceForSize, ccy)}` : null),
    tile('vsArrival', 'vs arrival', sc.improvementVsArrivalBps, fmtBps(sc.improvementVsArrivalBps), toneOf(sc.improvementVsArrivalBps),
      isNum(sc.arrivalPrice) ? `arrival ${formatQuoteAmount(sc.arrivalPrice, ccy)}` : null),
  ];
}

// ----- savings waterfall ----------------------------------------------------------------------

function steps(defs) {
  let cursor = 0;
  return defs.map(({ key, label, value, isTotal }) => {
    const v = isNum(value) ? value : null;
    if (isTotal) return { key, label, value: v, start: 0, end: v, isTotal: true };
    const start = cursor;
    const end = v === null ? null : cursor + v;
    if (v !== null) cursor = end;
    return { key, label, value: v, start, end, isTotal: false };
  });
}

const closeEnough = (a, b) => Math.abs(a - b) < 1e-6;

/**
 * The two waterfall series plus drift-as-context. `reconciles` is false when the server's net does
 * not equal improvement + fees saved (surfaced, never silently trusted); `complete` is false when
 * any savings step is null.
 */
export function savingsWaterfall(scorecard) {
  const sc = scorecard ?? {};
  const savings = steps([
    { key: 'improvement', label: 'Price improvement vs sweep', value: sc.priceImprovementQuote },
    { key: 'feesSaved', label: 'Fees saved (maker vs taker)', value: sc.feesSaved },
    { key: 'net', label: 'Net savings', value: sc.netSavingsQuote, isTotal: true },
  ]);
  const executionCost = isNum(sc.implementationShortfallQuote) && isNum(sc.opportunityCostQuote)
    ? sc.implementationShortfallQuote - sc.opportunityCostQuote
    : null;
  const shortfall = steps([
    { key: 'executionCost', label: 'Execution cost vs arrival', value: executionCost },
    { key: 'opportunityCost', label: 'Opportunity cost (unfilled)', value: sc.opportunityCostQuote },
    { key: 'is', label: 'Implementation shortfall', value: sc.implementationShortfallQuote, isTotal: true },
  ]);
  const complete = savings.every((s) => s.value !== null);
  const reconciles = complete && closeEnough(savings[0].value + savings[1].value, savings[2].value);
  const drift = isNum(sc.marketDriftBps)
    ? { bps: sc.marketDriftBps, quote: isNum(sc.marketDriftQuote) ? sc.marketDriftQuote : null, against: sc.marketDriftBps > 0 }
    : null;
  return { savings, shortfall, drift, complete, reconciles, notes: Array.isArray(sc.notes) ? sc.notes : [] };
}

// ----- pace -----------------------------------------------------------------------------------

const byPlacedAt = (a, b) => (toMs(a.placedAt) ?? 0) - (toMs(b.placedAt) ?? 0);

/** Percent of schedule elapsed vs percent filled per child, with the ±band and posture escalations. */
export function paceSeries(children, parent) {
  const kids = Array.isArray(children) ? [...children].sort(byPlacedAt) : [];
  const start = toMs(parent?.startedAt);
  const endBy = toMs(parent?.endBy);
  const durationMs = start !== null && endBy !== null && endBy > start
    ? endBy - start
    : isNum(parent?.durationSeconds) ? parent.durationSeconds * 1000 : null;
  const target = isNum(parent?.targetQty) && parent.targetQty > 0 ? parent.targetQty : null;

  const points = kids.map((c) => {
    const t = toMs(c.placedAt);
    const pctTime = t !== null && start !== null && durationMs ? ((t - start) / durationMs) * 100 : null;
    const pctFilled = target && isNum(c.cumulativeFilledQty) ? (c.cumulativeFilledQty / target) * 100 : null;
    return {
      t,
      pctTime,
      pctFilled,
      upper: pctTime === null ? null : pctTime + PACE_BAND_PCT,
      lower: pctTime === null ? null : pctTime - PACE_BAND_PCT,
      posture: isNum(c.posture) ? c.posture : null,
      postureName: c.postureName ?? (isNum(c.posture) ? POSTURE_NAMES[c.posture] : null) ?? null,
      paceBandOffsetPct: isNum(c.paceBandOffsetPct) ? c.paceBandOffsetPct : null,
      childOrderId: c.childOrderId,
    };
  });

  const escalations = [];
  for (let i = 1; i < points.length; i++) {
    const from = points[i - 1].posture;
    const to = points[i].posture;
    // A smaller PricingStrategy short is MORE aggressive (MatchMarketSize = 0).
    if (from !== null && to !== null && to < from) {
      escalations.push({ index: i, from, to, fromName: POSTURE_NAMES[from], toName: POSTURE_NAMES[to] });
    }
  }
  return { points, escalations, band: PACE_BAND_PCT };
}

// ----- execution tape -------------------------------------------------------------------------

function fillStyle(isMaker) {
  if (isMaker === true) return 'maker';
  if (isMaker === false) return 'taker';
  return 'unknown';
}

/**
 * The 1-minute tape as a low/high band with close as the mid proxy (labelled — there is no bid/ask
 * tape), the children's bid/ask at placement, one marker per filled child (hollow = maker, solid =
 * taker, grey = unknown), and the reference lines that exist.
 */
export function tapeSeries(tape, children, parent, scorecard) {
  const sc = scorecard ?? {};
  const band = (Array.isArray(tape) ? tape : [])
    .map((p) => ({ t: toMs(p.time), lo: p.low, hi: p.high, mid: p.close }))
    .filter((p) => p.t !== null && isNum(p.mid));
  const kids = Array.isArray(children) ? [...children].sort(byPlacedAt) : [];
  const fills = kids
    .filter((c) => isNum(c.filledQty) && c.filledQty > 0 && isNum(c.avgFillPrice))
    .map((c) => ({
      t: toMs(c.resolvedAt ?? c.placedAt),
      price: c.avgFillPrice,
      qty: c.filledQty,
      style: fillStyle(c.isMaker),
      posture: c.posture ?? null,
      childOrderId: c.childOrderId,
    }))
    .filter((f) => f.t !== null);
  const quotes = kids
    .filter((c) => isNum(c.bidAtPlace) && isNum(c.askAtPlace))
    .map((c) => ({ t: toMs(c.placedAt), bid: c.bidAtPlace, ask: c.askAtPlace }))
    .filter((q) => q.t !== null);

  const arrival = sc.arrivalPrice ?? parent?.arrivalPrice;
  const avgFill = sc.avgFillPrice ?? parent?.avgFillPrice;
  const lines = [
    { key: 'arrival', label: 'arrival', value: arrival },
    { key: 'avgFill', label: 'avg fill', value: avgFill },
    { key: 'vwap', label: 'interval VWAP (approx. 1 m OHLCV)', value: sc.intervalVwap },
    { key: 'final', label: 'final', value: sc.endPrice },
  ].filter((l) => isNum(l.value));

  const start = toMs(parent?.startedAt);
  const end = toMs(parent?.endedAt ?? parent?.endBy);
  return {
    band,
    quotes,
    fills,
    lines,
    midLabel: 'mid ≈ 1-minute close (no bid/ask tape); band = candle low–high',
    window: start !== null && end !== null ? { start, end } : null,
  };
}

// ----- posture mix ----------------------------------------------------------------------------

/** Filled qty grouped by posture, most-filled first. */
export function postureMix(children) {
  const acc = new Map();
  for (const c of Array.isArray(children) ? children : []) {
    if (!isNum(c.filledQty) || c.filledQty <= 0 || !isNum(c.posture)) continue;
    const cur = acc.get(c.posture) ?? { posture: c.posture, postureName: c.postureName ?? POSTURE_NAMES[c.posture] ?? String(c.posture), qty: 0 };
    cur.qty += c.filledQty;
    acc.set(c.posture, cur);
  }
  const total = [...acc.values()].reduce((s, m) => s + m.qty, 0);
  return [...acc.values()]
    .sort((a, b) => b.qty - a.qty)
    .map((m) => ({ ...m, pct: total > 0 ? (m.qty / total) * 100 : null }));
}

// ----- child blotter --------------------------------------------------------------------------

/** Display rows for the child blotter (every child field kept, plus derived latency/labels/texts). */
export function blotterRows(children, parent) {
  const ccy = parent?.quoteCurrency ?? 'USD';
  return (Array.isArray(children) ? children : []).map((c) => {
    const placed = toMs(c.placedAt);
    const resolved = toMs(c.resolvedAt);
    const latencyMs = placed !== null && resolved !== null ? resolved - placed : null;
    return {
      ...c,
      latencyMs,
      makerLabel: fillStyle(c.isMaker),
      postureName: c.postureName ?? (isNum(c.posture) ? POSTURE_NAMES[c.posture] : null) ?? null,
      feeSavedText: fmtQuote(c.feeSaved, ccy),
      feesText: fmtQuote(c.fees, ccy, { signed: false }),
      avgFillText: isNum(c.avgFillPrice) ? formatQuoteAmount(c.avgFillPrice, ccy) : '—',
      limitText: isNum(c.limitPrice) ? formatQuoteAmount(c.limitPrice, ccy) : '—',
      vsMidText: fmtBps(c.improvementVsMidBps),
      paceText: isNum(c.paceBandOffsetPct) ? fmtPct(c.paceBandOffsetPct, { decimals: 1 }) : '—',
      filledText: `${fmtQty(c.filledQty)} / ${fmtQty(c.targetQty)}`,
    };
  });
}

/** Stable sort by key (numbers or strings), nulls last in either direction. */
export function sortRows(rows, key, dir = 'asc') {
  const sign = dir === 'desc' ? -1 : 1;
  return [...(rows ?? [])].sort((a, b) => {
    const av = a?.[key];
    const bv = b?.[key];
    const aNull = av === null || av === undefined;
    const bNull = bv === null || bv === undefined;
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sign;
    return String(av).localeCompare(String(bv)) * sign;
  });
}

// ----- manual blotter grouping ----------------------------------------------------------------

const SIDE_OF_ACTION = { 1: 'Buy', 2: 'Sell' };

function progressOf(summary) {
  if (!summary) return { filledQty: null, targetQty: null, pct: null, cyclesCompleted: null, cyclesPlanned: null };
  const filledQty = isNum(summary.filledQty) ? summary.filledQty : null;
  const targetQty = isNum(summary.targetQty) ? summary.targetQty : null;
  let pct = null;
  if (targetQty && filledQty !== null) pct = (filledQty / targetQty) * 100;
  else if (isNum(summary.targetAmount) && summary.targetAmount > 0 && isNum(summary.filledAmount)) {
    pct = (summary.filledAmount / summary.targetAmount) * 100;
  }
  return {
    filledQty,
    targetQty,
    pct: pct === null ? null : Math.min(100, Math.max(0, pct)),
    cyclesCompleted: isNum(summary.cyclesCompleted) ? summary.cyclesCompleted : null,
    cyclesPlanned: isNum(summary.cyclesPlanned) ? summary.cyclesPlanned : null,
  };
}

/**
 * Fold venue orders into parent groups for the manual blotter. `orders` are the venue's
 * ResponseOrder rows (contract: Data.Models/ExchangeOrder.cs — `orderId`, `parentOrderId`,
 * `enableTWAP`); `parents` are TwapParentSummary rows. Groups keep first-seen order; summaries
 * with no venue row yet (a Working parent before its first child) are appended as their own group.
 */
/**
 * The venue row's TWAP flag. The list endpoint serializes `ExchangeOrder.EnableTWAP` with the host's
 * System.Text.Json camelCase policy, which lowers only the leading run → `enableTWAP`; the other two
 * spellings are accepted defensively (a Newtonsoft-serialized or PascalCase payload) so a casing drift
 * can never hide a parent among the singles.
 */
function isTwapParentFlag(o) {
  return o.enableTWAP === true || o.enableTwap === true || o.EnableTWAP === true;
}

/**
 * Stable, UNIQUE keys for a keyed `{#each}` (Svelte 5 throws on a duplicate or undefined key): the
 * item's own id when present, an index fallback when it is missing, and a `#n` suffix on a repeat.
 * Returns `[{ key, item }]` in the input order.
 */
export function withUniqueKeys(items, keyOf) {
  const seen = new Map();
  return (Array.isArray(items) ? items : []).map((item, i) => {
    const base = keyOf(item, i);
    const raw = base === null || base === undefined || base === '' ? `idx-${i}` : String(base);
    const n = seen.get(raw) ?? 0;
    seen.set(raw, n + 1);
    return { key: n === 0 ? raw : `${raw}#${n}`, item };
  });
}

export function groupChildren(orders, parents) {
  const summaries = new Map((Array.isArray(parents) ? parents : []).map((p) => [p.parentOrderId, p]));
  const groups = [];
  const byId = new Map();

  const groupFor = (id) => {
    let g = byId.get(id);
    if (!g) {
      const summary = summaries.get(id) ?? null;
      g = {
        kind: 'parent', id, order: null, summary, children: [],
        symbol: summary?.symbol ?? null, side: summary?.side ?? null, status: summary?.status ?? null,
        progress: progressOf(summary),
      };
      byId.set(id, g);
      groups.push(g);
    }
    return g;
  };

  for (const o of Array.isArray(orders) ? orders : []) {
    if (!o) continue;
    if (o.parentOrderId) {
      const g = groupFor(o.parentOrderId);
      g.children.push(o);
      g.symbol ??= o.symbol ?? null;
      g.side ??= SIDE_OF_ACTION[o.action] ?? null;
    } else if (isParentId(o.orderId) || (isTwapParentFlag(o) && !o.parentOrderId)) {
      const g = groupFor(o.orderId);
      g.order = o;
      g.symbol ??= o.symbol ?? null;
      g.side ??= SIDE_OF_ACTION[o.action] ?? null;
    } else {
      groups.push({ kind: 'single', order: o });
    }
  }
  for (const [id] of summaries) if (!byId.has(id)) groupFor(id);
  return groups;
}

// ----- narrative ------------------------------------------------------------------------------

/** One plain-English sentence; empty for no parent. */
export function narrative(parent, scorecard) {
  if (!parent) return '';
  const sell = sideSign(parent.side) < 0;
  const qty = fmtQty(isWorking(parent.status) ? parent.targetQty ?? parent.filledQty : parent.filledQty);
  const minutes = isNum(parent.durationSeconds) ? Math.round(parent.durationSeconds / 60) : null;
  const over = minutes !== null ? ` over ${minutes} min` : '';
  const ccy = parent.quoteCurrency ?? 'USD';

  if (isWorking(parent.status)) {
    return `${sell ? 'Selling' : 'Buying'} ${qty} ${parent.symbol}${over} — ${fmtQty(parent.filledQty)} filled so far; the scorecard is computed when the parent finishes.`;
  }

  const verb = sell ? 'Sold' : 'Bought';
  const slices = isNum(parent.childCount) ? ` in ${parent.childCount} slice${parent.childCount === 1 ? '' : 's'}` : '';
  const sc = scorecard ?? {};
  const maker = isNum(sc.makerFillPct) ? ` (${Math.round(sc.makerFillPct)} % maker)` : '';
  let saving = '';
  if (isNum(sc.netSavingsQuote)) {
    const pct = isNum(sc.netSavingsPct) ? ` (${fmtPct(sc.netSavingsPct).replace(/ [▲▼]$/, '')})` : '';
    saving = sc.netSavingsQuote >= 0
      ? `, saving ${formatQuoteAmount(sc.netSavingsQuote, ccy)}${pct} versus a market sweep`
      : `, costing ${formatQuoteAmount(-sc.netSavingsQuote, ccy)}${pct} versus a market sweep`;
  } else {
    saving = '; no scorecard is available';
  }
  let drift = '.';
  if (isNum(sc.marketDriftBps) && sc.marketDriftBps !== 0) {
    drift = `; the market moved ${sc.marketDriftBps > 0 ? 'against you' : 'in your favour'} by ${Math.abs(sc.marketDriftBps).toFixed(1)} bps over the window.`;
  }
  const headline = `${verb} ${qty} ${parent.symbol}${over}${slices}${maker}${saving}${drift}`;
  return [headline, qualitySentence(sc), reversionSentence(sc)].filter(Boolean).join(' ');
}

/** Sentence 2: pace adherence and/or the patience premium (PR-C2) — omitted when neither figure exists. */
function qualitySentence(sc) {
  const pace = isNum(sc.paceAdherence) ? `Slices stayed ${Math.round(sc.paceAdherence * 100)} % on schedule` : null;
  const premium = isNum(sc.patiencePremiumBps) ? sc.patiencePremiumBps : null;
  let patience = null;
  if (premium !== null) {
    const abs = Math.abs(premium).toFixed(1);
    patience = premium === 0
      ? 'patient and aggressive postures filled at the same price versus mid (no patience premium)'
      : `patient postures filled ${abs} bps ${premium > 0 ? 'better' : 'worse'} than aggressive ones (patience premium ${fmtBps(premium)})`;
  }
  if (pace && patience) return `${pace}, and ${patience}.`;
  if (pace) return `${pace}.`;
  if (patience) return `${patience[0].toUpperCase()}${patience.slice(1)}.`;
  return null;
}

/** Sentence 3: the 5-minute reversion once the server stamper has run — omitted until then. */
function reversionSentence(sc) {
  if (!isNum(sc.reversionBps5m)) return null;
  const abs = Math.abs(sc.reversionBps5m).toFixed(1);
  if (sc.reversionBps5m > 0) return `Five minutes after the last fill the market had moved a further ${abs} bps away from the fills — the order was ahead of the move.`;
  if (sc.reversionBps5m < 0) return `Five minutes after the last fill the price had reverted ${abs} bps back toward the fills — a sign of transient impact.`;
  return 'Five minutes after the last fill the price was back at the average fill.';
}

// ----- PR-C2: execution-quality tiles ---------------------------------------------------------

/** `420 ms` under a second, `30.0 s` above; `—` for a non-number. */
export function fmtLatency(ms) {
  if (!isNum(ms)) return '—';
  return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`;
}

function noteFor(sc, needle, fallback) {
  const notes = Array.isArray(sc?.notes) ? sc.notes : [];
  return notes.find((n) => typeof n === 'string' && n.toLowerCase().includes(needle)) ?? fallback;
}

/**
 * The report's execution-quality tiles (PR-C2): patience premium (contract: TwapScorecardResult.
 * PatiencePremiumBps — qty-weighted vs-mid improvement of patient postures minus aggressive ones; null
 * with a Notes line when either group is empty), pace adherence and the 5-minute reversion (stamped by
 * the server job, never by the SPA). A missing figure is an honest "—" whose sub carries the reason.
 */
export function qualityTiles(scorecard) {
  const sc = scorecard ?? {};
  const tile = (key, label, value, text, tone, sub, hint = null) => ({ key, label, value: isNum(value) ? value : null, text, tone, sub, hint });
  const premium = sc.patiencePremiumBps;
  const rev = sc.reversionBps5m;
  return [
    tile('patience', 'Patience premium', premium, fmtBps(premium), toneOf(premium),
      isNum(premium) ? 'patient postures vs aggressive, vs mid' : noteFor(sc, 'patience', 'needs fills under both patient and aggressive postures'),
      'qty-weighted improvement vs mid of slices filled under the patient postures minus the aggressive ones'),
    tile('pace', 'Pace adherence', sc.paceAdherence, isNum(sc.paceAdherence) ? fmtPct(sc.paceAdherence * 100, { signed: false, decimals: 1 }) : '—', 'neutral',
      isNum(sc.paceAdherence) ? '1 − mean |pace offset|' : noteFor(sc, 'pace', 'no slice carried a pace-band offset')),
    tile('reversion', 'Reversion 5 m after end', rev, fmtBps(rev), toneOf(rev),
      isNum(rev)
        ? (rev > 0 ? 'price kept moving away from the fills' : rev < 0 ? 'price reverted toward the fills (transient impact)' : 'price back at the average fill')
        : 'stamped five minutes after the last fill',
      'side·(price 5 min after terminal − avg fill)/avg fill; stamped from the 1-minute tape by the server'),
  ];
}
